import { CheckCircle, Play } from "@phosphor-icons/react";
import { useMutation, useQuery } from "convex/react";
import { useMemo, useState } from "react";

import { api } from "../../convex/_generated/api";
import { navigateToAppPath } from "../navigation";
import { WorkspaceSetupDetail } from "../workspace/WorkspaceSetupDetail";
import type { Id } from "../../convex/_generated/dataModel";
import type { BusinessMembership, CurrentMembership } from "./types";

type BusinessProfile =
  "APPOINTMENT_BASED_SERVICES" | "HOSPITALITY_RESERVATIONS";

type BusinessSetupSummary = {
  profile: string | null;
  onboardingStatus: string;
  connectionStatus: string;
  counts: Record<string, number>;
};

const defaultHours = [
  { dayOfWeek: 1, startMinute: 9 * 60, endMinute: 17 * 60 },
  { dayOfWeek: 2, startMinute: 9 * 60, endMinute: 17 * 60 },
  { dayOfWeek: 3, startMinute: 9 * 60, endMinute: 17 * 60 },
  { dayOfWeek: 4, startMinute: 9 * 60, endMinute: 17 * 60 },
  { dayOfWeek: 5, startMinute: 9 * 60, endMinute: 15 * 60 },
];

export function BusinessSetupPage({
  business,
  embedded = false,
  onSignOut,
}: {
  business: BusinessMembership;
  embedded?: boolean;
  onSignOut: () => void;
}) {
  const businessId = business.businessId as Id<"businesses">;
  const currentMembership = useQuery(api.memberships.getForBusiness, {
    businessId,
  }) as CurrentMembership | undefined;
  const summary = useQuery(api.businessSetup.getSetupSummary, { businessId });

  const panel = (
    <BusinessSetupPanel
      businessId={businessId}
      currentMembership={currentMembership ?? null}
      summary={summary}
    />
  );

  if (embedded) {
    return (
      <WorkspaceSetupDetail
        description="Configure the existing guided profile, services, resources and supported inventory."
        eyebrow="Business details"
        title={business.name ?? "Business details"}
      >
        {panel}
      </WorkspaceSetupDetail>
    );
  }

  return (
    <main className="foundation-shell business-setup-shell">
      <section aria-live="polite" className="status-panel">
        <span className="status-label">Guided setup</span>
        <strong>{business.name}</strong>
        <span>{business.role} workspace</span>
        <button onClick={onSignOut} type="button">
          Sign out
        </button>
      </section>
      {panel}
    </main>
  );
}

function BusinessSetupPanel({
  businessId,
  currentMembership,
  summary,
}: {
  businessId: Id<"businesses">;
  currentMembership: CurrentMembership | null;
  summary: BusinessSetupSummary | undefined;
}) {
  const startWizard = useMutation(api.businessSetup.startWizard);
  const completeSetup = useMutation(api.businessSetup.completeMinimumDataset);
  const [profile, setProfile] = useState<BusinessProfile>(
    "APPOINTMENT_BASED_SERVICES",
  );
  const [status, setStatus] = useState("Ready");
  const [pending, setPending] = useState(false);
  const clientRequestId = useMemo(
    () => `nk40-ui-${businessId}-${profile}`,
    [businessId, profile],
  );
  const owner = currentMembership?.role === "OWNER";

  async function runSetup() {
    if (!owner || pending) return;
    setPending(true);
    setStatus("Starting");
    await startWizard({ businessId, clientRequestId });
    setStatus("Completing");
    await completeSetup({
      businessId,
      clientRequestId,
      ...setupPayload(profile),
    });
    setStatus("Configured");
    setPending(false);
  }

  return (
    <section className="business-setup-panel" aria-live="polite">
      <BusinessSetupToolbar
        configured={summary?.onboardingStatus === "CONFIGURED"}
        onProfileChange={setProfile}
        onRunSetup={runSetup}
        owner={owner}
        pending={pending}
        profile={profile}
      />
      <BusinessSetupMetrics status={status} summary={summary} />
      <SetupStepNavigation />
    </section>
  );
}

function BusinessSetupToolbar({
  configured,
  onProfileChange,
  onRunSetup,
  owner,
  pending,
  profile,
}: {
  configured: boolean;
  onProfileChange: (profile: BusinessProfile) => void;
  onRunSetup: () => Promise<void>;
  owner: boolean;
  pending: boolean;
  profile: BusinessProfile;
}) {
  return (
    <div className="business-setup-toolbar">
      <label>
        <span className="status-label">Business profile</span>
        <select
          disabled={!owner || pending}
          onChange={(event) =>
            onProfileChange(event.target.value as BusinessProfile)
          }
          value={profile}
        >
          <option value="APPOINTMENT_BASED_SERVICES">
            Appointment services
          </option>
          <option value="HOSPITALITY_RESERVATIONS">
            Hospitality reservations
          </option>
        </select>
      </label>
      <button disabled={!owner || pending} onClick={onRunSetup} type="button">
        {configured ? (
          <CheckCircle aria-hidden size={18} />
        ) : (
          <Play aria-hidden size={18} />
        )}
        Configure
      </button>
    </div>
  );
}

function BusinessSetupMetrics({
  status,
  summary,
}: {
  status: string;
  summary: BusinessSetupSummary | undefined;
}) {
  const metrics = setupMetricsFor(summary, status);

  return (
    <div className="business-setup-metrics">
      {metrics.map((metric) => (
        <Metric key={metric.label} label={metric.label} value={metric.value} />
      ))}
    </div>
  );
}

function setupMetricsFor(
  summary: BusinessSetupSummary | undefined,
  status: string,
) {
  const visible = summary ?? {
    profile: null,
    onboardingStatus: status,
    connectionStatus: "Checking",
    counts: {},
  };

  return [
    { label: "Profile", value: visible.profile ?? "Not set" },
    { label: "Onboarding", value: visible.onboardingStatus },
    { label: "WhatsApp", value: visible.connectionStatus },
    { label: "Services", value: visible.counts.services ?? 0 },
    { label: "Resources", value: visible.counts.resources ?? 0 },
    { label: "Knowledge", value: visible.counts.knowledge ?? 0 },
    { label: "Inventory", value: visible.counts.inventoryUnits ?? 0 },
  ];
}

function SetupStepNavigation() {
  return (
    <nav className="business-setup-next" aria-label="Setup steps">
      <button
        onClick={() => navigateToAppPath("/app/configuration-assistant")}
        type="button"
      >
        Configuration assistant
      </button>
      <button
        onClick={() => navigateToAppPath("/app/capabilities")}
        type="button"
      >
        Capabilities
      </button>
      <button
        onClick={() => navigateToAppPath("/app/whatsapp-connection")}
        type="button"
      >
        WhatsApp connection
      </button>
      <button onClick={() => navigateToAppPath("/app/account")} type="button">
        Account settings
      </button>
    </nav>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="business-setup-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function setupPayload(profile: BusinessProfile) {
  const base = {
    profile,
    timezone: "Africa/Johannesburg",
    operatingHours: defaultHours,
    knowledgeEntries: [
      {
        entryType: "LOCATION" as const,
        title: "Primary location",
        body: "Johannesburg",
      },
      {
        entryType: "FAQ" as const,
        title: "Response policy",
        body: "Escalate uncertain requests to a human.",
      },
    ],
  };

  if (profile === "HOSPITALITY_RESERVATIONS") {
    return {
      ...base,
      services: [],
      hospitality: {
        propertyName: "Main property",
        inventoryTypeName: "Standard room",
        inventoryUnitName: "Standard room 1",
        capacity: 2,
      },
    };
  }

  return {
    ...base,
    resourceName: "Owner calendar",
    services: [
      {
        name: "Consultation",
        description: "Initial customer appointment",
        price: {
          kind: "FIXED_PRICE" as const,
          currency: "ZAR",
          amountMinor: 45000,
        },
        durationMinutes: 45,
        availabilityEligibility: "ELIGIBLE_FOR_AVAILABILITY" as const,
      },
    ],
  };
}
